import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { apiFetch, ApiError } from "@/lib/api-client";
import { Skeleton } from "@/components/skeleton";
import type { WorkflowEdge, WorkflowNode } from "@/hooks/use-workflows";
import { WorkflowEditor } from "@/routes/workflows/automations/editor";

interface SequenceListItem {
  id: string;
  name: string;
  status: string;
  currentVersion: number | null;
  triggers: Array<{ type: string; eventType?: string }>;
  updatedAt: string;
}

interface SequenceDetail {
  id: string;
  name: string;
  status: string;
  currentVersion: number | null;
  triggers: Array<{ type: string; eventType?: string }>;
  reentryPolicy: string;
  quietHours: Record<string, unknown> | null;
  frequencyCap: Record<string, unknown> | null;
  updatedAt: string;
}

interface SequenceVersion {
  id: string;
  version: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  publishedAt: string;
}

interface EnrollmentSummary {
  id: string;
  status: string;
  currentNodeId: string | null;
  startedAt: string;
  completedAt: string | null;
}

interface WorkflowExecutionLog {
  nodeId: string;
  action: string;
}

interface SequenceStepMetric {
  nodeId: string;
  label: string;
  type: string;
  eventType: string | null;
  completed: number;
  waiting: number;
  errors: number;
}

const GENERATOR_STEP_OPTIONS = [3, 4, 5, 6, 7] as const;
const GENERATOR_CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
] as const;

type GeneratorChannel = (typeof GENERATOR_CHANNEL_OPTIONS)[number]["value"];

function getNodeLabel(node: WorkflowNode) {
  const configLabel = node.config.label;
  if (typeof configLabel === "string" && configLabel.trim().length > 0) {
    return configLabel;
  }

  const subjectOverride = node.config.subjectOverride;
  if (typeof subjectOverride === "string" && subjectOverride.trim().length > 0) {
    return subjectOverride;
  }

  return String(node.type).replace(/_/g, " ");
}

function buildSequenceSignals(
  nodes: WorkflowNode[],
  enrollments: EnrollmentSummary[],
  logsByEnrollment: WorkflowExecutionLog[][],
) {
  const completedByNode = new Map<string, number>();
  const errorsByNode = new Map<string, number>();
  const waitingByNode = enrollments.reduce<Map<string, number>>((acc, enrollment) => {
    if (!enrollment.currentNodeId) {
      return acc;
    }

    acc.set(enrollment.currentNodeId, (acc.get(enrollment.currentNodeId) ?? 0) + 1);
    return acc;
  }, new Map());

  for (const log of logsByEnrollment.flat()) {
    if (!log.nodeId) {
      continue;
    }

    if (log.action === "advanced" || log.action === "completed") {
      completedByNode.set(log.nodeId, (completedByNode.get(log.nodeId) ?? 0) + 1);
    }

    if (log.action === "errored") {
      errorsByNode.set(log.nodeId, (errorsByNode.get(log.nodeId) ?? 0) + 1);
    }
  }

  const stepMetrics: SequenceStepMetric[] = nodes
    .filter((node) => String(node.type) !== "trigger")
    .map((node) => ({
      nodeId: node.id,
      label: getNodeLabel(node),
      type: String(node.type),
      eventType: typeof node.config.eventType === "string" ? node.config.eventType : null,
      completed: completedByNode.get(node.id) ?? 0,
      waiting: waitingByNode.get(node.id) ?? 0,
      errors: errorsByNode.get(node.id) ?? 0,
    }));

  const trackingNodeIds = new Set(
    nodes
      .filter((node) => {
        const eventType = String(node.config.eventType ?? "");
        return eventType === "email_opened"
          || eventType === "email_clicked"
          || eventType === "email_replied"
          || eventType === "list_unsubscribed";
      })
      .map((node) => node.id),
  );

  const trackingMetrics = stepMetrics.filter((metric) => trackingNodeIds.has(metric.nodeId));

  return {
    timedSteps: nodes.filter((node) => String(node.type).includes("wait")).length,
    hasReplyExit: nodes.some((node) => String(node.config.eventType ?? "").includes("replied")),
    hasUnsubscribeExit: nodes.some((node) => String(node.config.eventType ?? "").includes("unsubscribed")),
    hasTracking: nodes.some((node) => {
      const eventType = String(node.config.eventType ?? "");
      return eventType.includes("opened") || eventType.includes("clicked");
    }),
    stepMetrics,
    trackingMetrics,
  };
}

export default function EmailSequencesPage() {
  const { sequenceId } = useParams<{ sequenceId?: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sequences, setSequences] = useState<SequenceListItem[]>([]);
  const [detail, setDetail] = useState<SequenceDetail | null>(null);
  const [version, setVersion] = useState<SequenceVersion | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentSummary[]>([]);
  const [logsByEnrollment, setLogsByEnrollment] = useState<WorkflowExecutionLog[][]>([]);
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatorPrompt, setGeneratorPrompt] = useState("");
  const [generatorStepCount, setGeneratorStepCount] = useState<(typeof GENERATOR_STEP_OPTIONS)[number]>(5);
  const [generatorChannels, setGeneratorChannels] = useState<GeneratorChannel[]>(["email", "sms"]);
  const [generatorError, setGeneratorError] = useState<string | null>(null);
  const [generatorDraft, setGeneratorDraft] = useState<{
    name: string;
    description: string;
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreatingGenerated, setIsCreatingGenerated] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        if (!sequenceId) {
          const response = await apiFetch<{ workflows: SequenceListItem[] }>("/email-marketing/workflows");
          if (!active) return;
          setSequences(response.workflows ?? []);
          setDetail(null);
          setVersion(null);
          setEnrollments([]);
          setLogsByEnrollment([]);
          return;
        }

        const [workflowResponse, versionResponse, enrollmentResponse] = await Promise.all([
          apiFetch<{ workflow: SequenceDetail }>(`/email-marketing/workflows/${sequenceId}`),
          apiFetch<{ version: SequenceVersion }>(`/email-marketing/workflows/${sequenceId}/version`),
          apiFetch<{ enrollments: EnrollmentSummary[] }>(`/email-marketing/workflows/${sequenceId}/enrollments?limit=25`),
        ]);

        const enrollmentLogs = await Promise.all(
          (enrollmentResponse.enrollments ?? []).map(async (enrollment) => {
            const response = await apiFetch<{ log: WorkflowExecutionLog[] }>(
              `/email-marketing/workflows/${sequenceId}/enrollments/${enrollment.id}/log?limit=100`,
            );
            return response.log ?? [];
          }),
        );

        if (!active) return;
        setDetail(workflowResponse.workflow);
        setVersion(versionResponse.version);
        setEnrollments(enrollmentResponse.enrollments ?? []);
        setLogsByEnrollment(enrollmentLogs);
      } catch (err) {
        if (!active) return;
        setError(err instanceof ApiError ? err.message : "Failed to load sequences");
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [sequenceId]);

  const sequenceSignals = useMemo(
    () => buildSequenceSignals(version?.nodes ?? [], enrollments, logsByEnrollment),
    [version?.nodes, enrollments, logsByEnrollment],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-28 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-5 text-sm text-[var(--color-foreground)]">
        {error}
      </div>
    );
  }

  if (!sequenceId) {
    return (
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Email Sequences</h1>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Workflow-backed nurture flows with mounted builder, runtime telemetry, and per-step tracking.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowGenerator(true)}
              className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white"
            >
              Generate Sequence
            </button>
          </div>
        </div>
        {showGenerator ? (
          <SequenceGeneratorModal
            prompt={generatorPrompt}
            onPromptChange={setGeneratorPrompt}
            stepCount={generatorStepCount}
            onStepCountChange={setGeneratorStepCount}
            selectedChannels={generatorChannels}
            onSelectedChannelsChange={setGeneratorChannels}
            draft={generatorDraft}
            onDraftChange={setGeneratorDraft}
            error={generatorError}
            isGenerating={isGenerating}
            isCreating={isCreatingGenerated}
            onClose={() => {
              setShowGenerator(false);
              setGeneratorError(null);
              setGeneratorDraft(null);
            }}
            onGenerate={async () => {
              setIsGenerating(true);
              setGeneratorError(null);
              try {
                const response = await apiFetch<{ draft: { name: string; description: string; nodes: WorkflowNode[]; edges: WorkflowEdge[] } }>(
                  "/email-marketing/workflows/generate",
                  {
                    method: "POST",
                    body: JSON.stringify({
                      goal: generatorPrompt,
                      vertical: "services",
                      channels: generatorChannels,
                      timeframeDays: 14,
                      maxNodes: generatorStepCount,
                    }),
                  },
                );
                setGeneratorDraft(response.draft);
              } catch (caughtError) {
                setGeneratorError(caughtError instanceof ApiError ? caughtError.message : "Failed to generate sequence.");
              } finally {
                setIsGenerating(false);
              }
            }}
            onCreate={async () => {
              if (!generatorDraft) {
                return;
              }

              setIsCreatingGenerated(true);
              setGeneratorError(null);
              try {
                const workflow = await apiFetch<{ workflow: { id: string } }>("/email-marketing/workflows", {
                  method: "POST",
                  body: JSON.stringify({
                    name: generatorDraft.name,
                    triggers: [{ type: "manual" }],
                  }),
                });

                await apiFetch(`/email-marketing/workflows/${workflow.workflow.id}/publish`, {
                  method: "POST",
                  body: JSON.stringify({
                    nodes: generatorDraft.nodes,
                    edges: generatorDraft.edges,
                    changeSummary: "Generated from Ghost Sequence wizard",
                  }),
                });

                setShowGenerator(false);
                navigate(`/email/sequences/${workflow.workflow.id}`);
              } catch (caughtError) {
                setGeneratorError(caughtError instanceof ApiError ? caughtError.message : "Failed to create generated sequence.");
              } finally {
                setIsCreatingGenerated(false);
              }
            }}
          />
        ) : null}
        {sequences.map((sequence) => (
          <div
            key={sequence.id}
            className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[var(--color-foreground)]">{sequence.name}</h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {sequence.status} · Version {sequence.currentVersion ?? 0} · Updated {new Date(sequence.updatedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-medium text-[var(--color-primary)]">
                {sequence.triggers[0]?.type ?? "workflow"}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 border-t border-[var(--color-border)] pt-4">
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Open the detail view to edit the graph, inspect enrollments, and review per-step tracking.
              </p>
              <Link
                to={`/email/sequences/${sequence.id}`}
                className="inline-flex items-center rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
              >
                Open sequence
              </Link>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Workflow-backed sequence builder</h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Review the live workflow graph, exit guards, and enrollment activity before changing the sequence.
        </p>
      </div>

      {detail && version ? (
        <WorkflowEditor
          workflowId={detail.id}
          workflowName={detail.name}
          initialNodes={version.nodes}
          initialEdges={version.edges}
          onBack={() => navigate("/email/sequences")}
          onPublish={async (workflowId, nodes, edges) => {
            await apiFetch(`/email-marketing/workflows/${workflowId}/publish`, {
              method: "POST",
              body: JSON.stringify({
                nodes,
                edges,
                changeSummary: "Edited from Stero email sequence builder",
              }),
            });
          }}
          onDryRun={null}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Sequence Runtime Signals</h2>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3">
              <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Timed steps</div>
              <div className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{sequenceSignals.timedSteps}</div>
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3">
              <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Live enrollments</div>
              <div className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{enrollments.length} live enrollments</div>
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-foreground)]">
              {sequenceSignals.hasReplyExit ? "Reply exit configured" : "Reply exit not configured"}
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-foreground)]">
              {sequenceSignals.hasUnsubscribeExit ? "Unsubscribe exit configured" : "Unsubscribe exit not configured"}
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-foreground)]">
              {sequenceSignals.hasTracking ? "Open / click tracking" : "Tracking nodes missing"}
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-foreground)]">
              Exit guards
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Exit Audit</h2>
          <div className="mt-4 space-y-3 text-sm text-[var(--color-muted-foreground)]">
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3">
              Reply exits observed: {
                sequenceSignals.trackingMetrics.find((metric) => metric.eventType === "email_replied")?.completed ?? 0
              }
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3">
              Unsubscribe exits observed: {
                sequenceSignals.trackingMetrics.find((metric) => metric.eventType === "list_unsubscribed")?.completed ?? 0
              }
            </div>
            <div className="stagger-item rounded-lg bg-[var(--color-surface)] px-4 py-3">
              Re-entry policy: {detail?.reentryPolicy ?? "unknown"}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Per-Step Tracking</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Aggregated from live enrollment logs across the latest published sequence version.
            </p>
          </div>
          <div className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-medium text-[var(--color-primary)]">
            {logsByEnrollment.length} tracked enrollments
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {sequenceSignals.stepMetrics.map((metric) => (
            <div key={metric.nodeId} className="stagger-item rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-[var(--color-foreground)]">{metric.label}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                    {metric.type.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-full bg-[var(--color-background)] px-2.5 py-1 text-xs text-[var(--color-muted-foreground)]">
                  node {metric.nodeId}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-md bg-[var(--color-background)] px-3 py-2">
                  <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Completed</div>
                  <div className="mt-1 font-semibold text-[var(--color-foreground)]">{metric.completed}</div>
                </div>
                <div className="rounded-md bg-[var(--color-background)] px-3 py-2">
                  <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Waiting</div>
                  <div className="mt-1 font-semibold text-[var(--color-foreground)]">{metric.waiting}</div>
                </div>
                <div className="rounded-md bg-[var(--color-background)] px-3 py-2">
                  <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Errors</div>
                  <div className="mt-1 font-semibold text-[var(--color-foreground)]">{metric.errors}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SequenceGeneratorModal({
  prompt,
  onPromptChange,
  stepCount,
  onStepCountChange,
  selectedChannels,
  onSelectedChannelsChange,
  draft,
  onDraftChange,
  error,
  isGenerating,
  isCreating,
  onGenerate,
  onCreate,
  onClose,
}: {
  prompt: string;
  onPromptChange: (value: string) => void;
  stepCount: (typeof GENERATOR_STEP_OPTIONS)[number];
  onStepCountChange: (value: (typeof GENERATOR_STEP_OPTIONS)[number]) => void;
  selectedChannels: GeneratorChannel[];
  onSelectedChannelsChange: (value: GeneratorChannel[]) => void;
  draft: { name: string; description: string; nodes: WorkflowNode[]; edges: WorkflowEdge[] } | null;
  onDraftChange: (value: { name: string; description: string; nodes: WorkflowNode[]; edges: WorkflowEdge[] } | null) => void;
  error: string | null;
  isGenerating: boolean;
  isCreating: boolean;
  onGenerate: () => Promise<void>;
  onCreate: () => Promise<void>;
  onClose: () => void;
}) {
  function toggleChannel(channel: GeneratorChannel) {
    if (selectedChannels.includes(channel)) {
      if (selectedChannels.length === 1) {
        return;
      }
      onSelectedChannelsChange(selectedChannels.filter((entry) => entry !== channel));
      return;
    }

    onSelectedChannelsChange([...selectedChannels, channel]);
  }

  function updateDraftNode(nodeId: string, updater: (node: WorkflowNode) => WorkflowNode) {
    if (!draft) {
      return;
    }

    onDraftChange({
      ...draft,
      nodes: draft.nodes.map((node) => (node.id === nodeId ? updater(node) : node)),
    });
  }

  function renderDraftNodeEditor(node: WorkflowNode) {
    if (node.type === "send_email" || node.type === "send_message") {
      return (
        <div className="mt-3 space-y-2">
          <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Subject line
            <input
              type="text"
              value={typeof node.config.subjectOverride === "string" ? node.config.subjectOverride : ""}
              onChange={(event) => {
                updateDraftNode(node.id, (currentNode) => ({
                  ...currentNode,
                  config: {
                    ...currentNode.config,
                    subjectOverride: event.target.value,
                  },
                }));
              }}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
          </label>
          <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Body preview
            <textarea
              value={typeof node.config.body === "string" ? node.config.body : ""}
              onChange={(event) => {
                updateDraftNode(node.id, (currentNode) => ({
                  ...currentNode,
                  config: {
                    ...currentNode.config,
                    body: event.target.value,
                  },
                }));
              }}
              rows={3}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
          </label>
        </div>
      );
    }

    if (node.type === "wait_timed") {
      return (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Timing
            <input
              type="number"
              min={1}
              value={typeof node.config.duration === "number" ? node.config.duration : 1}
              onChange={(event) => {
                updateDraftNode(node.id, (currentNode) => ({
                  ...currentNode,
                  config: {
                    ...currentNode.config,
                    duration: Math.max(1, Number(event.target.value) || 1),
                  },
                }));
              }}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
          </label>
          <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Unit
            <select
              value={typeof node.config.unit === "string" ? node.config.unit : "days"}
              onChange={(event) => {
                updateDraftNode(node.id, (currentNode) => ({
                  ...currentNode,
                  config: {
                    ...currentNode.config,
                    unit: event.target.value,
                  },
                }));
              }}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </label>
        </div>
      );
    }

    return null;
  }

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-foreground)]">Ghost Sequence Generator</h2>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Describe the outcome and let Stero draft the first workflow graph for operator review.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-sm text-[var(--color-muted-foreground)]">Close</button>
      </div>
      <textarea
        value={prompt}
        onChange={(event) => onPromptChange(event.target.value)}
        rows={4}
        placeholder="Example: Nurture new cosmetic dentistry leads to book within 14 days with one reminder and one proof point."
        className="mt-4 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
      />
      <div className="mt-4 grid gap-4 lg:grid-cols-[180px_1fr]">
        <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
          Step count
          <select
            value={stepCount}
            onChange={(event) => onStepCountChange(Number(event.target.value) as (typeof GENERATOR_STEP_OPTIONS)[number])}
            className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {GENERATOR_STEP_OPTIONS.map((option) => (
              <option key={option} value={option}>{option} steps</option>
            ))}
          </select>
        </label>
        <div>
          <div className="text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Channel preference</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {GENERATOR_CHANNEL_OPTIONS.map((option) => {
              const isSelected = selectedChannels.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleChannel(option.value)}
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    isSelected
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] text-[var(--color-foreground)]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={prompt.trim().length < 10 || isGenerating}
          onClick={() => void onGenerate()}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {isGenerating ? "Generating..." : "Generate draft"}
        </button>
      </div>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {draft ? (
        <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="text-sm font-semibold text-[var(--color-foreground)]">{draft.name}</div>
          <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{draft.description}</div>
          <div className="mt-4 space-y-2">
            {draft.nodes.map((node) => (
              <div key={node.id} className="rounded-md bg-[var(--color-background)] px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-[var(--color-foreground)]">
                      {String(node.config.label ?? node.type)}
                    </div>
                    <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">{node.type}</div>
                  </div>
                  {typeof node.config.channel === "string" ? (
                    <div className="rounded-full bg-[var(--color-primary)]/10 px-2 py-1 text-[11px] font-medium text-[var(--color-primary)]">
                      {node.config.channel}
                    </div>
                  ) : null}
                </div>
                {renderDraftNodeEditor(node)}
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onDraftChange(null)}
              className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)]"
            >
              Reject draft
            </button>
            <button
              type="button"
              disabled={isCreating}
              onClick={() => void onCreate()}
              className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {isCreating ? "Creating..." : "Accept and create"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
